import { AppGate } from '@lib/routing-engine';
import {
  combine,
  createEvent,
  createStore,
  sample,
  useStoreMap,
} from '@lib/state-engine';
import { DiContainer } from '@@/di/di-container';
import { ClientStorageService } from '@@/common/services/client-storage.service';
import { ISimpleMixingBag } from '@@/mixing/ts';
import { isValidBagId } from '@lib/bag-formatting';

const clientStorageService = DiContainer.get(ClientStorageService);

const STORAGE_L_BAG_KEY = 'L_BAG';
const STORAGE_R_BAG_KEY = 'R_BAG';

export const MAX_SIMPLE_MIXING_ITEMS_CNT = 2;

export const reset = createEvent();
export const addBag = createEvent<string | ISimpleMixingBag>();
export const addBagByIdx = createEvent<{ id: string, idx: number }>();
export const removeBag = createEvent<string>(); // bagId or 'left' | 'right'

/** Складские бункеры, добавленные в простой микс */
export const $mixedBags = createStore<MixedBags>([null, null]);
export const $mixedBagsCount = combine(
  $mixedBags,
  (bags) => bags.filter((b) => b).length,
);

export const isBagMixed = (id: string) =>
  useStoreMap({
    store: $mixedBags,
    keys: [id],
    fn: (bags, [id]) => {
      return bags.includes(id) ?? false
    }
  });

sample({
  clock: reset,
  fn: () => {
    clientStorageService.remove(STORAGE_L_BAG_KEY);
    clientStorageService.remove(STORAGE_R_BAG_KEY);

    return $mixedBags.defaultState;
  },
  target: $mixedBags,
});

sample({
  clock: removeBag,
  source: $mixedBags,
  fn: (bags, idOrDirection) => {
    const isId = isValidBagId(idOrDirection);
    const index = isId
      ? bags.findIndex((b) => b === idOrDirection)
      : idOrDirection === 'left'
      ? 0
      : 1;

    if (index === 0) {
      clientStorageService.remove(STORAGE_L_BAG_KEY);
      return [null, bags[1]] as MixedBags;
    }

    if (index === 1) {
      clientStorageService.remove(STORAGE_R_BAG_KEY);
      return [bags[0], null] as MixedBags;
    }

    return bags;
  },
  target: $mixedBags,
});

sample({
  clock: addBag,
  source: $mixedBags,
  fn: (bags, idOrBag) => {
    const emptyIndex = bags.findIndex((b) => !b);
    if (emptyIndex === 0) {
      clientStorageService.set(STORAGE_L_BAG_KEY, idOrBag);
      return [idOrBag, bags[1]] as MixedBags;
    }

    if (emptyIndex === 1) {
      clientStorageService.set(STORAGE_R_BAG_KEY, idOrBag);
      return [bags[0], idOrBag] as MixedBags;
    }

    return bags;
  },
  target: $mixedBags,
});

sample({
  clock: addBagByIdx,
  source: $mixedBags,
  fn: (bags, { id, idx}) => {
    if (idx === 0) {
      clientStorageService.set(STORAGE_L_BAG_KEY, id);
      return [id, bags[1]] as MixedBags;
    }

    if (idx === 1) {
      clientStorageService.set(STORAGE_R_BAG_KEY, id);
      return [bags[0], id] as MixedBags;
    }

    return bags;
  },
  target: $mixedBags,
});

sample({
  clock: AppGate.open,
  fn: () =>
    [
      clientStorageService.get<MixedBags[0]>(STORAGE_L_BAG_KEY),
      clientStorageService.get<MixedBags[1]>(STORAGE_R_BAG_KEY),
    ] as MixedBags,
  target: $mixedBags,
});

export type MixedBags = [
  string | ISimpleMixingBag | null,
  string | ISimpleMixingBag | null,
];
